'use client';
import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import { Sparkles, BookOpen, Clock, ArrowRight, Tag, ChevronLeft, User } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

type BlogBlock =
  | { type: 'p'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'ul'; items: string[] };

function BlogBlocks({ blocks }: { blocks: BlogBlock[] }) {
  return (
    <div className="prose-content space-y-5 text-sm text-muted-foreground leading-relaxed">
      {blocks.map((block, i) => {
        if (block.type === 'h3') {
          return (
            <h3 key={i} className="text-base font-700 text-foreground mt-6 mb-2">
              {block.text}
            </h3>
          );
        }
        if (block.type === 'ul') {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1.5">
              {block.items.map((item, j) => (
                <li key={j}>
                  <strong className="text-foreground">{item.split(' — ')[0]}</strong>
                  {item.includes(' — ') ? ` — ${item.split(' — ').slice(1).join(' — ')}` : ''}
                </li>
              ))}
            </ul>
          );
        }
        return <p key={i}>{block.text}</p>;
      })}
    </div>
  );
}

export default function BlogContent() {
  const { t } = useLanguage();
  const bp = t?.blogPage;
  const [activeCategory, setActiveCategory] = useState('all');
  const [openPostId, setOpenPostId] = useState<string | null>(null);

  const posts = bp?.posts ?? [];
  const categoryKeys = bp?.categoryKeys ?? [];

  const filterTabs = useMemo(
    () => [
      { key: 'all', label: bp?.categoryAll ?? 'All' },
      ...categoryKeys.map((key) => ({
        key,
        label: bp?.categories?.[key as keyof typeof bp.categories] ?? key,
      })),
    ],
    [bp, categoryKeys]
  );

  const filtered =
    activeCategory === 'all'
      ? posts
      : posts.filter((p) => p.categoryKey === activeCategory);
  const featured = posts.find((p) => p.featured);
  const rest = filtered.filter((p) => !p.featured);
  const openPost = posts.find((p) => p.id === openPostId) ?? null;

  if (openPost) {
    const categoryLabel =
      bp?.categories?.[openPost.categoryKey as keyof typeof bp.categories] ??
      openPost.categoryKey;
    return (
      <main className="min-h-screen bg-background pt-24 pb-20">
        <section className="max-w-screen-md mx-auto px-6 lg:px-10">
          <button
            type="button"
            onClick={() => setOpenPostId(null)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ChevronLeft size={15} /> {bp?.backToBlog}
          </button>
          <div className="mb-2">
            <span className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-600">
              {categoryLabel}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-800 text-foreground mb-4 leading-tight">
            {openPost.title}
          </h1>
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-8 pb-6 border-b border-border">
            <span className="flex items-center gap-1">
              <User size={12} />
              {openPost.author}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {openPost.readTime}
            </span>
            <span>{openPost.date}</span>
          </div>
          <BlogBlocks blocks={(openPost.blocks ?? []) as BlogBlock[]} />
          <div className="mt-10 pt-8 border-t border-border">
            <div className="rounded-2xl bg-muted/50 border border-border p-6 text-center">
              <h2 className="text-base font-700 text-foreground mb-2">{bp?.ctaTitle}</h2>
              <p className="text-muted-foreground text-sm mb-4">{bp?.ctaDesc}</p>
              <Link
                href="/sign-up-login-screen"
                className="btn-primary px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2"
              >
                <Sparkles size={14} /> {bp?.ctaButton}
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background pt-24 pb-20">
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-14">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <BookOpen size={13} className="text-primary" />
            <span className="text-xs font-600 text-primary">{bp?.badge}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-800 text-foreground mb-5 leading-tight">
            {bp?.titleBefore}{' '}
            <span className="gradient-text">{bp?.titleHighlight}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">{bp?.subtitle}</p>
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-10">
        <div className="flex flex-wrap gap-2">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveCategory(tab.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-500 border transition-colors ${
                activeCategory === tab.key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {activeCategory === 'all' && featured && (
        <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-10">
          <div className="glass-card rounded-2xl p-8 md:p-10 border border-border">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-600">
                {bp?.categories?.[featured.categoryKey as keyof typeof bp.categories]}
              </span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Sparkles size={11} className="text-primary" /> {bp?.featured}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-700 text-foreground mb-3 leading-snug">
              {featured.title}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6 max-w-2xl">
              {featured.excerpt}
            </p>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <User size={12} />
                  {featured.author}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {featured.readTime}
                </span>
                <span>{featured.date}</span>
              </div>
              <button
                type="button"
                onClick={() => setOpenPostId(featured.id)}
                className="inline-flex items-center gap-1.5 text-sm text-primary font-500 hover:gap-2.5 transition-all"
              >
                {bp?.readArticle} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
        {rest.length === 0 && (
          <p className="text-muted-foreground text-sm">{bp?.emptyCategory}</p>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((post) => (
            <div
              key={post.id}
              className="glass-card rounded-xl p-6 border border-border flex flex-col"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-full bg-muted border border-border text-xs font-500 text-muted-foreground flex items-center gap-1">
                  <Tag size={10} />
                  {bp?.categories?.[post.categoryKey as keyof typeof bp.categories]}
                </span>
              </div>
              <h3 className="text-base font-700 text-foreground mb-2 leading-snug">{post.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-4">
                {post.excerpt}
              </p>
              <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto pt-4 border-t border-border/50">
                <span className="flex items-center gap-1">
                  <Clock size={11} />
                  {post.readTime}
                </span>
                <button
                  type="button"
                  onClick={() => setOpenPostId(post.id)}
                  className="text-primary font-500 flex items-center gap-1 hover:gap-2 transition-all"
                >
                  {bp?.read} <ArrowRight size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="rounded-2xl bg-muted/50 border border-border p-8 text-center">
          <h2 className="text-xl font-700 text-foreground mb-2">{bp?.ctaTitle}</h2>
          <p className="text-muted-foreground text-sm mb-6">{bp?.ctaDesc}</p>
          <Link
            href="/sign-up-login-screen"
            className="btn-primary px-6 py-2.5 rounded-xl text-sm inline-flex items-center gap-2"
          >
            <Sparkles size={14} /> {bp?.ctaButton}
          </Link>
        </div>
      </section>
    </main>
  );
}
